import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { computeChannelCoverage, channelCoverageSummary } from "@/lib/channel-coverage";
import { listingApprovalNotice, listingApprovalState } from "@/lib/listing-approval-semantics";
import { connectorDisplayName } from "@/lib/connector-identity";
import { indexBcListingStorefrontPaths, indexConnectorStorefront, listingLiveLinks } from "@/lib/listing-live-display";
import { ViewLiveLink } from "@/components/feature/ViewLiveLink";

export default function ChannelSummaryPanel({
  orgId,
  productId,
  onManage,
}: {
  orgId: string;
  productId: string;
  onManage?: () => void;
}) {
  const { data } = useQuery({
    queryKey: ["product-channel-summary", orgId, productId],
    enabled: !!orgId && !!productId,
    queryFn: async () => {
      const [connectors, listings, bc, bcConfig] = await Promise.all([
        supabase.from("connectors").select("id, name, provider, auth_status").eq("org_id", orgId),
        supabase.from("channel_listings").select("id, connector_id, publication_status, status, last_error, dirty_domains, external_listing_id, approved_version, intended_version, operator_revision").eq("org_id", orgId).eq("product_id", productId),
        supabase.from("bigcommerce_products").select("listing_id, external_product_id, storefront_custom_url").eq("org_id", orgId).eq("product_id", productId),
        supabase.from("connector_bigcommerce_config").select("connector_id, storefront_origin, store_hash").eq("org_id", orgId),
      ]);
      const bcByListing: Record<string, string> = {};
      for (const row of (bc.data ?? []) as { listing_id: string; external_product_id: number | null }[]) {
        if (row.external_product_id) bcByListing[row.listing_id] = String(row.external_product_id);
      }
      const pathByListing = indexBcListingStorefrontPaths(
        (bc.data ?? []) as { listing_id: string; storefront_custom_url: string | null }[],
      );
      const listingSource = (listings.data ?? []) as {
        id: string;
        connector_id: string;
        publication_status: string | null;
        status: string | null;
        last_error: string | null;
        dirty_domains: string[] | null;
        external_listing_id: string | null;
        approved_version: number | null;
        intended_version: number | null;
        operator_revision: number | null;
      }[];
      const customUrlByConnector: Record<string, string> = {};
      const listingRows = listingSource.map((l) => {
        const path = pathByListing[l.id];
        if (path) customUrlByConnector[l.connector_id] = path;
        return {
          listingId: l.id,
          connectorId: l.connector_id,
          publicationStatus: l.publication_status,
          status: l.status,
          hasDirty: (l.dirty_domains?.length ?? 0) > 0,
          dirtyDomains: l.dirty_domains,
          lastError: l.last_error,
          externalListingId: bcByListing[l.id] ?? l.external_listing_id,
          providerConfirmedListed: Boolean(bcByListing[l.id] || l.external_listing_id),
          /**
           * A channel draft created by the wizard is NOT approval-ready. Its
           * operator revision is tracked separately from the catalogue version,
           * so approval is read from the persisted columns, never assumed.
           */
          approvalNotice: listingApprovalNotice(listingApprovalState({
            approved_version: l.approved_version,
            intended_version: l.intended_version ?? 0,
            operator_revision: l.operator_revision,
            dirty_domains: l.dirty_domains,
          })),
        };
      });
      const badges = computeChannelCoverage(
        ((connectors.data ?? []) as { id: string; name: string; provider: string; auth_status: string }[]).map((c) => {
          const cfg = ((bcConfig.data ?? []) as { connector_id: string; storefront_origin: string | null; store_hash: string | null }[]).find((r) => r.connector_id === c.id);
          return {
            id: c.id,
            name: connectorDisplayName({
              id: c.id,
              name: c.name,
              provider: c.provider,
              authStatus: c.auth_status,
              storeHash: cfg?.store_hash,
              storefrontOrigin: cfg?.storefront_origin,
            }),
            provider: c.provider,
            authStatus: c.auth_status,
          };
        }),
        listingRows,
      );
      return {
        badges,
        summary: channelCoverageSummary(badges),
        listingByConnector: Object.fromEntries(listingRows.map((l) => [l.connectorId, l])),
        storefront: indexConnectorStorefront(
          (bcConfig.data ?? []) as { connector_id: string; storefront_origin: string | null; store_hash: string | null }[],
        ),
        customUrlByConnector,
      };
    },
  });

  if (!data || data.badges.length === 0) return null;
  const listed = data.summary.listedCount;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Selling on {listed} channel{listed === 1 ? "" : "s"}</h3>
        {onManage ? (
          <button type="button" onClick={onManage} className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Manage</button>
        ) : (
          <Link to={`/products/${productId}?tab=channels`} className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Manage</Link>
        )}
      </div>
      <ul className="space-y-2">
        {data.badges.map((b) => {
          const live = listingLiveLinks({
            provider: b.provider,
            externalId: b.liveExternalId,
            storefrontOrigin: data.storefront.originByConnector[b.connectorId],
            customUrl: data.customUrlByConnector[b.connectorId],
          }).liveUrl;
          const approvalNotice = data.listingByConnector[b.connectorId]?.approvalNotice ?? null;
          return (
            <li key={b.connectorId} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-800">{b.name}</span>
              <span className="flex items-center gap-3">
                <span className={b.matrixKind === "error" ? "text-red-700" : b.matrixKind === "paused" ? "text-amber-700" : b.listed ? "text-emerald-700" : b.matrixKind === "needs_sync" ? "text-amber-700" : "text-slate-400"}>{b.matrixLabel}</span>
                {approvalNotice && !b.listed && (
                  <span data-testid={`channel-approval-${b.connectorId}`} className="text-xs text-amber-700">{approvalNotice}</span>
                )}
                {b.listed ? (
                  <ViewLiveLink href={live} />
                ) : b.matrixKind === "not_listed" ? (
                  onManage ? (
                    <button type="button" onClick={onManage} className="text-xs font-medium text-emerald-700">Create listing</button>
                  ) : (
                    <Link to={`/products/${productId}?tab=channels`} className="text-xs font-medium text-emerald-700">Create listing</Link>
                  )
                ) : data.listingByConnector[b.connectorId]?.listingId ? (
                  <Link
                    to={`/listings/${data.listingByConnector[b.connectorId].listingId}`}
                    className="text-xs font-medium text-amber-700"
                  >
                    Review listing
                  </Link>
                ) : (
                  onManage ? (
                    <button type="button" onClick={onManage} className="text-xs font-medium text-amber-700">Review listing</button>
                  ) : (
                    <Link to={`/products/${productId}?tab=channels`} className="text-xs font-medium text-amber-700">Review listing</Link>
                  )
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
